// Single source of truth for the "related content" Cypher query. Exported for reuse via the
// package subpath `@atlassian/rovo-platform-ui-components/inferred-project-card/related-content-cypher`
// so consumers (e.g. `@atlassian/inferred-project-page`) do not maintain a divergent copy.
//
// NOTE: this string is coupled to the shape parsed by callers (the `kind`/`node`/`ts`/`via` columns
// and the entity fields selected by the GraphQL query in `RelatedContentSection.tsx`). Changing the
// returned columns or entity types here requires updating every consumer's query and row parsing.

// ARIs only ever contain these characters, so anything else is treated as an injection attempt
// rather than escaped: the value is interpolated into a Cypher string literal.
const SAFE_ARI = /^ari:[A-Za-z0-9:_./-]+$/;

const isSafeCypherAri = (value: string): boolean => SAFE_ARI.test(value);

const quoteAri = (ari: string): string => {
	if (!isSafeCypherAri(ari)) {
		throw new Error('Refusing to build a Cypher query from an unsafe ARI');
	}
	return `"${ari}"`;
};

const project = (projectAri: string): string =>
	`(project:InferredProject {ari: ${quoteAri(projectAri)}})`;

/** Row `kind` tags returned by {@link buildRelatedCypher}. */
export type RelatedRowKind = 'document' | 'child' | 'related' | 'pullRequest';

const RELATED_ROW_KIND = {
	document: 'document',
	child: 'child',
	related: 'related',
	pullRequest: 'pullRequest',
} as const satisfies Record<string, RelatedRowKind>;

/**
 * Second hop from the entities Rovo linked to the project: documents that reference them, child and
 * related issues, and pull requests. Every row is tagged with a `kind` column so a single
 * `UNION ALL` query hydrates every related type, and returns the linked entity it was reached
 * through (`via`) so a UI can say "Child of KRONOS-786". Relationship timestamps are only read where
 * the graph stores them as timestamps; `issue_associated_pr` does not, and reading it as one fails
 * serialisation, so pull requests return `0 AS ts`.
 *
 * The rows are intentionally not ordered here: consumers rank them client-side (by recency, with
 * type coverage) because pull requests have no timestamp (`ts = 0`) and a DB-side `ORDER BY ts` would
 * push them out of a truncated page before that ranking can run.
 */
export const buildRelatedCypher = (projectAri: string): string =>
	[
		`MATCH ${project(projectAri)}-[:inferred_project_links_entity]->(entity)-[rel:content_referenced_entity]->(document)`,
		`RETURN "${RELATED_ROW_KIND.document}" AS kind, document AS node, entity AS via, rel.lastUpdated AS ts`,
		'UNION ALL',
		`MATCH ${project(projectAri)}-[:inferred_project_links_entity]->(entity)-[rel:parent_issue_has_child_issue]->(issue)`,
		`RETURN "${RELATED_ROW_KIND.child}" AS kind, issue AS node, entity AS via, rel.lastUpdated AS ts`,
		'UNION ALL',
		`MATCH ${project(projectAri)}-[:inferred_project_links_entity]->(entity)-[rel:issue_related_to_issue]->(issue)`,
		`RETURN "${RELATED_ROW_KIND.related}" AS kind, issue AS node, entity AS via, rel.lastUpdated AS ts`,
		'UNION ALL',
		`MATCH ${project(projectAri)}-[:inferred_project_links_entity]->(entity)-[:issue_associated_pr]->(pullRequest)`,
		`RETURN "${RELATED_ROW_KIND.pullRequest}" AS kind, pullRequest AS node, entity AS via, 0 AS ts`,
	].join('\n');
